import React from 'react';
import clsx from 'clsx';
import Dropdown from '../../../_custom/Dropdown';
import { Input } from '../../../_custom';
import { numberOnly } from '../../../../utils/utils';
import {
    ADDRESS_VISIT_NOTE,
    BUSINESS_DETAILS_KEY,
    OWNED_BY_OPTIONS,
    OWNED_BY_VALUES,
    OWNED_PROPERTY_ADDRESS_MAX_LENGTH,
    OWNED_PROPERTY_FIELD_KEYS,
    OWNED_PROPERTY_LABELS,
    OWNED_PROPERTY_RELATIONSHIP_OPTIONS,
    PINCODE_DIGIT_LENGTH,
} from '../../DealDrawerConstants';
import { isOwnedPropertyPincodeValid } from './ownedPropertyTypes';
import styles from '../../ManageDealDrawer.module.scss';

interface OwnedPropertySectionProps {
    formData: Record<string, any>;
    handleChange: (key: string, value: any, importanceKey?: string | null) => void;
    readonly?: boolean;
}

const ownedPropertyFieldKey = (key: string) => `${BUSINESS_DETAILS_KEY}.${key}`;

// Shown after Applicant Details when every entered address ownership comes back "all rented"
// (ApplicationFormStep computes the predicate). Never gates Proceed, and a hidden block's stored
// values are left untouched - this component simply never unmounts formData.
export const OwnedPropertySection: React.FC<OwnedPropertySectionProps> = ({ formData, handleChange, readonly }) => {
    const addressKey = ownedPropertyFieldKey(OWNED_PROPERTY_FIELD_KEYS.ADDRESS);
    const pincodeKey = ownedPropertyFieldKey(OWNED_PROPERTY_FIELD_KEYS.PINCODE);
    const ownedByKey = ownedPropertyFieldKey(OWNED_PROPERTY_FIELD_KEYS.OWNED_BY);
    const relationshipKey = ownedPropertyFieldKey(OWNED_PROPERTY_FIELD_KEYS.RELATIONSHIP);

    const addressValue = formData[addressKey] || '';
    const pincodeValue = formData[pincodeKey] || '';
    const ownedByValue = formData[ownedByKey] || null;
    const relationshipValue = formData[relationshipKey] || '';

    // Self has no relationship - switching away from Family member deletes the stored key.
    const handleOwnedByChange = (id: string) => {
        handleChange(ownedByKey, id || '');
        if (id !== OWNED_BY_VALUES.FAMILY_MEMBER) handleChange(relationshipKey, '');
    };

    return (
        <div className={styles.Section}>
            <div className={styles.SectionHeading}>{OWNED_PROPERTY_LABELS.heading}</div>
            <div className={styles.AddressVisitNote}>{OWNED_PROPERTY_LABELS.body}</div>

            <div className={styles.InputList}>
                <div className={styles.InputItem}>
                    <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{OWNED_PROPERTY_LABELS.addressLabel}</div>
                    <div className={styles.FieldFullWidthWrapper}>
                        <Input
                            type="text"
                            value={addressValue}
                            placeholder={OWNED_PROPERTY_LABELS.addressPlaceholder}
                            onChange={(value: string) => handleChange(addressKey, value.slice(0, OWNED_PROPERTY_ADDRESS_MAX_LENGTH))}
                            containerClassName={styles.FieldControlSm}
                            disabled={readonly}
                            allowAllCharacters
                            showFloatingLabel={false}
                        />
                        <div className={styles.AddressVisitNote}>{ADDRESS_VISIT_NOTE}</div>
                    </div>
                </div>

                <div className={styles.InputItem}>
                    <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{OWNED_PROPERTY_LABELS.pincodeLabel}</div>
                    <div className={styles.FieldFullWidthWrapper}>
                        <Input
                            type="text"
                            value={pincodeValue}
                            placeholder={OWNED_PROPERTY_LABELS.pincodePlaceholder}
                            onChange={(value: string) => handleChange(pincodeKey, numberOnly(value).slice(0, PINCODE_DIGIT_LENGTH))}
                            errorMessage={OWNED_PROPERTY_LABELS.invalidPincodeMessage}
                            isValid={isOwnedPropertyPincodeValid(pincodeValue)}
                            containerClassName={styles.FieldControlSm}
                            disabled={readonly}
                            showFloatingLabel={false}
                        />
                    </div>
                </div>

                <div className={styles.InputItem}>
                    <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{OWNED_PROPERTY_LABELS.ownedByLabel}</div>
                    <div className={styles.FieldFullWidthWrapper}>
                        <Dropdown
                            selectedOption={OWNED_BY_OPTIONS.find((option) => option.id === ownedByValue) ?? null}
                            optionsList={OWNED_BY_OPTIONS}
                            placeholder={OWNED_PROPERTY_LABELS.ownedByPlaceholder}
                            itemClickHandler={(option) => handleOwnedByChange(String(option.id || ''))}
                            style={{ width: '100%', height: '44px' }}
                            disabled={readonly}
                            showFloatingLabel={false}
                        />
                    </div>
                </div>

                {ownedByValue === OWNED_BY_VALUES.FAMILY_MEMBER && (
                    <div className={styles.InputItem}>
                        <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{OWNED_PROPERTY_LABELS.relationshipLabel}</div>
                        <div className={styles.FieldFullWidthWrapper}>
                            <Dropdown
                                selectedOption={relationshipValue ? { id: relationshipValue, label: relationshipValue } : null}
                                optionsList={OWNED_PROPERTY_RELATIONSHIP_OPTIONS}
                                placeholder={OWNED_PROPERTY_LABELS.relationshipPlaceholder}
                                itemClickHandler={(option) => handleChange(relationshipKey, option.id || '')}
                                style={{ width: '100%', height: '44px' }}
                                disabled={readonly}
                                showFloatingLabel={false}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OwnedPropertySection;
